import { healthResponseSchema, type HealthResponse } from '@golive/contracts';

export interface AuthUser {
  sessionId: string;
  userId: string;
  organizationId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'PARTNER' | 'COLLABORATOR' | 'CLIENT';
}

export interface Company {
  id: string;
  name: string;
  legalName: string | null;
  vatNumber: string | null;
  taxCode: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  notes: string | null;
  status: 'PROSPECT' | 'CLIENT' | 'INACTIVE';
  version: number;
  locations?: CompanyLocation[];
  contacts?: CompanyContact[];
  customFieldValues?: Array<{ id: string; value: unknown; definition: { label: string } }>;
  _count?: { contacts: number; opportunities: number; engagements: number };
}

export interface CompanyLocation {
  id: string;
  type: 'HEADQUARTERS' | 'OPERATIONAL' | 'OTHER';
  label: string | null;
  address: string;
  city: string;
  postalCode: string;
  province: string | null;
  country: string;
}

export interface CompanyContact {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  isPrimary: boolean;
  clientUserId: string | null;
  version: number;
}

export type OpportunityStage =
  'NEW' | 'CONTACTED' | 'QUALIFIED' | 'PROPOSAL' | 'NEGOTIATION' | 'WON' | 'LOST';

export interface Opportunity {
  id: string;
  companyId: string;
  responsibleUserId: string;
  name: string;
  stage: OpportunityStage;
  valueCents: string;
  probability: number;
  expectedCloseDate: string | null;
  closeReason: string | null;
  convertedAt: string | null;
  version: number;
  company: { id: string; name: string };
  responsible: { id: string; firstName: string; lastName: string };
}

export interface PipelineColumn {
  stage: OpportunityStage;
  items: Opportunity[];
}

export type ProjectStatus = 'PLANNED' | 'ACTIVE' | 'COMPLETED' | 'ARCHIVED';
export type ProjectTrafficLight = 'GREEN' | 'YELLOW' | 'RED';
export type TaskType = 'STANDARD' | 'FOLLOW_UP' | 'MONITORING';
export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export interface WorkflowState {
  id: string;
  key: string;
  label: string;
  position: number;
  isInitial: boolean;
  isClosed: boolean;
  color: string | null;
}

export interface ProjectTask {
  id: string;
  projectId: string;
  parentTaskId: string | null;
  title: string;
  description: string | null;
  type: TaskType;
  priority: TaskPriority;
  stateId: string;
  state: WorkflowState;
  assigneeUserId: string | null;
  assignee: { id: string; firstName: string; lastName: string } | null;
  dueDate: string | null;
  progressPercentage: number;
  version: number;
  subtasks: Array<{ id: string; title: string; progressPercentage: number }>;
}

export interface OperationalProject {
  id: string;
  code: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  completionPercentage: number;
  resultPercentage: number;
  trafficLight: ProjectTrafficLight;
  trafficLightReason: string | null;
  templateSnapshot: Record<string, unknown> | null;
  version: number;
  capabilities?: {
    view: boolean;
    manage: boolean;
    publish: boolean;
    approve: boolean;
    generateAi: boolean;
  };
  availableAssignees?: Array<{ id: string; firstName: string; lastName: string }>;
  engagement: { company: { id: string; name: string } };
  workflowStates?: WorkflowState[];
  workflowTransitions?: Array<{ fromStateId: string; toStateId: string }>;
  phases?: Array<{ id: string; name: string; position: number }>;
  objectives?: Array<{ id: string; name: string; achieved: boolean }>;
  taskCategories?: Array<{ id: string; label: string; color: string | null }>;
  progressHistory?: Array<{
    id: string;
    completionPercentage: number;
    resultPercentage: number;
    trafficLight: ProjectTrafficLight;
    status: ProjectStatus;
    reason: string | null;
    createdAt: string;
    actor: { firstName: string; lastName: string };
  }>;
  journalEntries?: Array<{
    id: string;
    entryDate: string;
    content: string;
    task: { id: string; title: string } | null;
    author: { firstName: string; lastName: string };
  }>;
  _count?: { tasks: number; objectives: number };
}

export interface ProjectTemplate {
  id: string;
  name: string;
  description: string | null;
  version: number;
  active: boolean;
  configuration: ProjectTemplateConfiguration;
}

export interface ProjectTemplateConfiguration {
  phases: Array<{ key: string; name: string }>;
  objectives: Array<{ key: string; name: string; description?: string; phaseKey?: string }>;
  workflowStates: Array<{
    key: string;
    label: string;
    isInitial?: boolean;
    isClosed?: boolean;
    color?: string;
  }>;
  transitions: Array<{ from: string; to: string }>;
  categories: Array<{ key: string; label: string; color?: string }>;
}

export type CollaboratorVisibility = 'GLOBAL' | 'ASSIGNED';

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: AuthUser['role'];
  status: 'INVITED' | 'ACTIVE' | 'SUSPENDED';
  collaboratorVisibility: CollaboratorVisibility | null;
  version: number;
  createdAt: string;
  _count: { sessions: number };
}

export interface AdminInvitation {
  id: string;
  email: string;
  role: AuthUser['role'];
  collaboratorVisibility: CollaboratorVisibility | null;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export type CustomEntityType = 'PROJECT' | 'TASK';
export type CustomFieldType = 'TEXT' | 'NUMBER' | 'DATE' | 'BOOLEAN' | 'SELECT';

export interface CustomFieldDefinition {
  id: string;
  entityType: CustomEntityType;
  key: string;
  label: string;
  fieldType: CustomFieldType;
  options: unknown;
  required: boolean;
  active: boolean;
  version: number;
}

export interface ProjectPermissionCandidate {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  collaboratorVisibility: CollaboratorVisibility;
  permission: {
    id: string | null;
    projectId: string;
    userId: string;
    canManage: boolean;
    canPublish: boolean;
    canApprove: boolean;
    canGenerateAi: boolean;
    version: number;
  };
}

export interface OperationalSettings {
  version: number;
  reportDefaultTitle: string;
  reportDefaultSections: ReportSection[];
  notificationEmailChannelEnabled: boolean;
  notificationDeadlineLeadDays: number;
  emailFromName: string;
  emailFromAddress: string;
  emailDeliveryEnabled: boolean;
  emailSenderVerifiedConfirmed: boolean;
  aiModel: string;
  aiMonthlyBudgetEurCents: number;
  aiBudgetEnforced: boolean;
  aiInputEurMicrosPerMillion: number;
  aiOutputEurMicrosPerMillion: number;
  aiEnabled: boolean;
  providerStatus: { resendConfigured: boolean; openAiConfigured: boolean };
}

export interface OpenAiModelOption {
  id: string;
  label: string;
  description: string;
  recommended: boolean;
  availability: 'available' | 'unavailable' | 'unverified';
  compatibility: 'verified' | 'unverified';
  pricing: {
    inputEurMicrosPerMillion: number;
    outputEurMicrosPerMillion: number;
    asOf: string;
  } | null;
}

export interface OpenAiModelCatalog {
  models: OpenAiModelOption[];
  currentModel: string;
  source: 'live' | 'cache' | 'stale' | 'fallback';
  fetchedAt: string | null;
  warning: string | null;
}

export interface AuditEvent {
  id: string;
  actorUserId: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  changes: unknown;
  metadata: unknown;
  correlationId: string | null;
  createdAt: string;
}

export type PublishableContentType = 'PROJECT_PROGRESS' | 'TASK' | 'DOCUMENT' | 'REPORT';
export type PublicationStatus = 'DRAFT' | 'PUBLISHED' | 'REVOKED';

export interface PortalPublication {
  id: string;
  projectId: string;
  contentType: PublishableContentType;
  sourceEntityId: string;
  title: string;
  status: PublicationStatus;
  snapshot: Record<string, unknown> | null;
  version: number;
  publishedAt: string | null;
  revokedAt: string | null;
  recipients: Array<{
    user: { id: string; firstName: string; lastName: string; email: string };
  }>;
}

export interface InternalDocument {
  id: string;
  entityType: string;
  entityId: string;
  title: string;
  currentVersion: number;
  versions: Array<{
    id: string;
    originalName: string;
    mimeType: string;
    sizeBytes: string;
  }>;
  generatedReport?: { id: string; status: 'DRAFT' | 'APPROVED'; revision: number } | null;
}

export type ReportSection =
  'SUMMARY' | 'PROGRESS' | 'ACTIVITIES' | 'RESULTS' | 'ISSUES' | 'NEXT_ACTIONS' | 'CHARTS';

export interface ProjectReport {
  id: string;
  projectId: string;
  documentId: string;
  documentVersionId: string;
  previousReportId: string | null;
  title: string;
  sections: ReportSection[];
  status: 'DRAFT' | 'APPROVED';
  revision: number;
  version: number;
  approvedAt: string | null;
  createdAt: string;
  createdBy?: { firstName: string; lastName: string };
  approvedBy?: { firstName: string; lastName: string } | null;
  documentVersion?: { sizeBytes: string; sha256: string };
}

export type ReportScheduleFrequency = 'DISABLED' | 'WEEKLY' | 'MONTHLY' | 'CUSTOM';

export interface ProjectReportSchedule {
  id: string | null;
  projectId: string;
  frequency: ReportScheduleFrequency;
  customIntervalDays: number | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  titleTemplate: string;
  sections: ReportSection[];
  version: number;
}

export type EmailStatus =
  | 'DRAFT'
  | 'QUEUED'
  | 'SIMULATED'
  | 'SENT'
  | 'DELAYED'
  | 'DELIVERED'
  | 'BOUNCED'
  | 'COMPLAINED'
  | 'FAILED';

export interface ClientEmailMessage {
  id: string;
  projectId: string;
  sourceReportId: string | null;
  recipientUserId: string;
  recipientEmail: string;
  subject: string;
  textBody: string;
  status: EmailStatus;
  version: number;
  attempts: number;
  approvedAt: string | null;
  sentAt: string | null;
  createdAt: string;
  recipient?: { firstName: string; lastName: string; email: string };
  events?: Array<{ eventType: string; occurredAt: string; applied: boolean }>;
}

export type AiSourceKind = 'PROJECT' | 'TASKS' | 'JOURNAL';

export interface AiDraftContent {
  summary: string;
  activities: string[];
  results: string[];
  issues: string[];
  nextActions: string[];
  emailSubject: string;
  emailBody: string;
}

export interface AiStatus {
  enabled: boolean;
  provider: string;
  reason?: string;
  model?: string;
  storeResponse?: boolean;
  budgetEnforced?: boolean;
  monthlyLimitEur?: number | null;
  remainingEur?: number | null;
  consumedEur?: number;
  dataTreatment?: string;
}

export interface AiGenerationPreview {
  id: string;
  status: 'PREVIEW' | 'RESERVED' | 'COMPLETED' | 'FAILED';
  periodStart: string;
  periodEnd: string;
  sourceKinds: AiSourceKind[];
  sourceSummary: Record<string, unknown>;
  provider: string;
  model: string;
  storeResponse: false;
  estimatedInputTokens: number;
  maxOutputTokens: number;
  estimatedCostEur: number;
  expiresAt: string;
  version: number;
  dataTreatment: string;
}

export interface AiDraft {
  id: string;
  status: 'AI_GENERATED' | 'HUMAN_REVIEWED';
  content: AiDraftContent;
  version: number;
  reviewedAt: string | null;
  createdAt: string;
  createdBy: { firstName: string; lastName: string };
  reviewedBy: { firstName: string; lastName: string } | null;
  generation: {
    id: string;
    model: string;
    promptVersion: string;
    schemaVersion: string;
    periodStart: string;
    periodEnd: string;
    sourceKinds: AiSourceKind[];
    actualCostEur: number;
    inputTokens: number | null;
    outputTokens: number | null;
    storeResponse: false;
  };
  revisions: Array<{
    id: string;
    revision: number;
    aiGenerated: boolean;
    reviewNote: string | null;
    createdAt: string;
    createdBy: { firstName: string; lastName: string };
  }>;
  publicationAvailable: false;
  sendingAvailable: false;
}

export interface ClientPortalProject {
  id: string;
  code: string;
  name: string;
  companyName: string;
  publishedContentCount: number;
}

export interface ClientPublication {
  id: string;
  contentType: PublishableContentType;
  title: string;
  snapshot: Record<string, unknown>;
  publishedAt: string;
}

export interface ClientPortalProjectDetail {
  project: Omit<ClientPortalProject, 'publishedContentCount'>;
  progress: ClientPublication[];
  tasks: ClientPublication[];
  documents: ClientPublication[];
  reports: ClientPublication[];
}

export interface ClientProjectAccessCandidate {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  hasAccess: boolean;
}

export type EconomicRecordStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface EconomicUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'PARTNER' | 'COLLABORATOR';
}

export interface InternalHourlyRate {
  id: string;
  userId: string;
  rateCentsPerHour: string;
  validFrom: string;
  validTo: string | null;
  user: EconomicUser;
}

export interface ProjectBudget {
  projectId: string;
  currency: string;
  revenueBudgetCents: string;
  hoursBudgetMinutes: number;
  laborCostBudgetCents: string;
  expenseBudgetCents: string;
  externalCostBudgetCents: string;
  timeTrackingRequired: boolean;
  version: number;
}

export interface TimeEntry {
  id: string;
  projectId: string;
  taskId: string | null;
  userId: string;
  workDate: string;
  minutes: number;
  note: string | null;
  version: number;
  user?: EconomicUser;
  task?: { id: string; title: string } | null;
  appliedHourlyRateId?: string;
  appliedRateCentsPerHour?: string;
  laborCostCents?: string;
}

export interface ProjectExpense {
  id: string;
  description: string;
  competenceDate: string;
  amountCents: string;
  status: EconomicRecordStatus;
  decisionNote: string | null;
  version: number;
  submittedBy?: EconomicUser;
}

export interface ExternalCost {
  id: string;
  description: string;
  supplierName: string | null;
  competenceDate: string;
  amountCents: string;
  status: EconomicRecordStatus;
  decisionNote: string | null;
  version: number;
}

export interface ProjectRevenue {
  id: string;
  description: string;
  competenceDate: string;
  amountCents: string;
  version: number;
}

export interface EconomicsSummary {
  project: { id: string; code: string; name: string };
  currency: 'EUR';
  budget: ProjectBudget | null;
  actuals: {
    revenueCents: string;
    trackedMinutes: number;
    laborCostCents: string;
    approvedExpenseCents: string;
    approvedExternalCostCents: string;
    marginCents: string;
  };
  completeness: {
    complete: boolean;
    missingClosedTaskCount: number;
    warnings: string[];
  };
  timeEntries: TimeEntry[];
  expenses: ProjectExpense[];
  externalCosts: ExternalCost[];
  revenues: ProjectRevenue[];
}

export type CalendarItemKind = 'EVENT' | 'TASK' | 'FOLLOW_UP';

export interface CalendarParticipant {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: 'PARTNER' | 'COLLABORATOR';
}

export interface CalendarItem {
  id: string;
  sourceId: string;
  kind: CalendarItemKind;
  title: string;
  start: string;
  end?: string;
  allDay: boolean;
  editable: boolean;
  color: string;
  extendedProps: {
    description?: string | null;
    location?: string | null;
    version?: number;
    company?: { id: string; name: string } | null;
    project?: { id: string; code: string; name: string } | null;
    task?: { id: string; title: string; type: TaskType } | null;
    participants?: CalendarParticipant[];
    assignee?: { id: string; firstName: string; lastName: string } | null;
    taskType?: TaskType;
  };
}

export interface CalendarEventInput {
  title: string;
  description?: string;
  location?: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  companyId?: string;
  projectId?: string;
  taskId?: string;
  participantUserIds: string[];
}

export interface CalendarFeedStatus {
  active: boolean;
  createdAt: string | null;
  lastUsedAt: string | null;
}

export interface DashboardProjectRow {
  id: string;
  code: string;
  name: string;
  company: { id: string; name: string };
  status: ProjectStatus;
  completionPercentage: number;
  resultPercentage: number;
  trafficLight: ProjectTrafficLight;
  openTasks: number;
  overdueTasks: number;
  dueSoonTasks: number;
}

export interface PortfolioDashboard {
  period: { from: string; to: string; timezone: string; dueSoonDays: number };
  summary: {
    totalProjects: number;
    activeProjects: number;
    byTrafficLight: { green: number; yellow: number; red: number };
    averageCompletionPercentage: number;
    averageResultPercentage: number;
    overdueTasks: number;
    dueSoonTasks: number;
  };
  projects: DashboardProjectRow[];
  partnerMetrics?: {
    pipeline: { openOpportunities: number; valueCents: string; weightedValueCents: string };
    economics: {
      revenueCents: string;
      laborCostCents: string;
      expenseCents: string;
      externalCostCents: string;
      marginCents: string;
      pendingApprovals: number;
    };
  };
}

export interface MyWorkDashboard {
  period: PortfolioDashboard['period'];
  summary: {
    openTasks: number;
    overdueTasks: number;
    followUps: number;
    monitorings: number;
    events: number;
  };
  tasks: Array<{
    id: string;
    title: string;
    type: TaskType;
    priority: TaskPriority;
    dueDate: string | null;
    progressPercentage: number;
    state: string;
    project: { id: string; code: string; name: string };
  }>;
  events: Array<{
    id: string;
    title: string;
    startsAt: string;
    endsAt: string;
    allDay: boolean;
    project: { id: string; code: string; name: string } | null;
  }>;
}

export type NotificationType =
  'TASK_ASSIGNMENT' | 'DEADLINE' | 'CRITICAL_CHANGE' | 'APPROVAL' | 'PUBLICATION';

export interface InternalNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  readAt: string | null;
  createdAt: string;
  link: string | null;
}

export interface NotificationPreference {
  type: NotificationType;
  internalEnabled: boolean;
  emailEnabled: boolean;
}

export interface SearchResultItem {
  id: string;
  title: string;
  context: string;
  href: string;
}

export interface GlobalSearchResponse {
  query: string;
  groups: {
    companies: SearchResultItem[];
    projects: SearchResultItem[];
    tasks: SearchResultItem[];
    publications: SearchResultItem[];
  };
}

export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

function cookie(name: string): string | undefined {
  const prefix = `${name}=`;
  return document.cookie
    .split(';')
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix))
    ?.slice(prefix.length);
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method?.toUpperCase() ?? 'GET';
  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');
  if (init?.body && !(init.body instanceof FormData))
    headers.set('Content-Type', 'application/json');
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = cookie('golive_csrf');
    if (csrf) headers.set('x-csrf-token', csrf);
  }
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: 'same-origin',
    headers,
  });
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as { detail?: string };
    throw new ApiError(
      response.status,
      problem.detail ?? `Richiesta non riuscita (${response.status})`,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function getHealth(signal?: AbortSignal): Promise<HealthResponse> {
  const response = await fetch('/api/v1/health', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw new Error(`Health check non disponibile (${response.status})`);
  return healthResponseSchema.parse(await response.json());
}

export function login(email: string, password: string) {
  return api<
    | { mfaRequired: false; user: AuthUser }
    | { mfaRequired: true; challengeToken: string; expiresAt: string }
  >('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
}

export function verifyMfa(challengeToken: string, code: string) {
  return api<{ user: AuthUser }>('/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({ challengeToken, code }),
  });
}

export function acceptInvitation(input: {
  token: string;
  firstName: string;
  lastName: string;
  password: string;
}) {
  return api<{ user: AuthUser }>('/auth/invitations/accept', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function requestPasswordReset(email: string) {
  return api<{ accepted: true }>('/auth/password-reset/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function completePasswordReset(token: string, password: string) {
  return api<void>('/auth/password-reset/complete', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

export function getMe() {
  return api<AuthUser>('/auth/me');
}

export function logout() {
  return api<void>('/auth/logout', { method: 'POST' });
}

export function globalSearch(query: string) {
  return api<GlobalSearchResponse>(`/search?q=${encodeURIComponent(query)}`);
}

export function listCompanies(search = '', status = '') {
  const params = new URLSearchParams({
    page: '1',
    pageSize: '100',
    sortBy: 'name',
    sortOrder: 'asc',
  });
  if (search) params.set('search', search);
  if (status) params.set('status', status);
  return api<{ items: Company[]; total: number }>(`/companies?${params.toString()}`);
}

export function getCompany(companyId: string) {
  return api<Company>(`/companies/${companyId}`);
}

export function createCompany(input: {
  name: string;
  legalName?: string;
  vatNumber?: string;
  taxCode?: string;
  email?: string;
  phone?: string;
  website?: string;
  notes?: string;
}) {
  return api<Company>('/companies', { method: 'POST', body: JSON.stringify(input) });
}

export function updateCompany(
  companyId: string,
  input: {
    version: number;
    name?: string;
    legalName?: string;
    vatNumber?: string;
    taxCode?: string;
    email?: string | null;
    phone?: string;
    website?: string | null;
    notes?: string;
    status?: Company['status'];
  },
) {
  return api<Company>(`/companies/${companyId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteCompany(companyId: string, version: number) {
  return api<void>(`/companies/${companyId}?version=${version}`, { method: 'DELETE' });
}

export type CompanyLocationInput = Omit<CompanyLocation, 'id'>;

export function createCompanyLocation(companyId: string, input: CompanyLocationInput) {
  return api<CompanyLocation>(`/companies/${companyId}/locations`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateCompanyLocation(
  companyId: string,
  locationId: string,
  input: CompanyLocationInput,
) {
  return api<CompanyLocation>(`/companies/${companyId}/locations/${locationId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteCompanyLocation(companyId: string, locationId: string) {
  return api<void>(`/companies/${companyId}/locations/${locationId}`, { method: 'DELETE' });
}

export type CompanyContactInput = Omit<CompanyContact, 'id' | 'version' | 'clientUserId'>;

export function createCompanyContact(companyId: string, input: CompanyContactInput) {
  return api<CompanyContact>(`/companies/${companyId}/contacts`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateCompanyContact(
  companyId: string,
  contactId: string,
  version: number,
  input: CompanyContactInput,
) {
  return api<CompanyContact>(`/companies/${companyId}/contacts/${contactId}`, {
    method: 'PATCH',
    body: JSON.stringify({ ...input, version }),
  });
}

export function deleteCompanyContact(companyId: string, contactId: string, version: number) {
  return api<void>(`/companies/${companyId}/contacts/${contactId}?version=${version}`, {
    method: 'DELETE',
  });
}

export function listPipeline() {
  return api<PipelineColumn[]>('/opportunities/pipeline/view');
}

export function createOpportunity(input: {
  companyId: string;
  responsibleUserId: string;
  name: string;
  valueCents: number;
  probability: number;
  expectedCloseDate?: string;
}) {
  return api<Opportunity>('/opportunities', { method: 'POST', body: JSON.stringify(input) });
}

export function changeOpportunityStage(
  opportunityId: string,
  version: number,
  stage: OpportunityStage,
  closeReason?: string,
) {
  return api<Opportunity>(`/opportunities/${opportunityId}/stage`, {
    method: 'POST',
    body: JSON.stringify({ version, stage, ...(closeReason ? { closeReason } : {}) }),
  });
}

export function convertOpportunity(
  opportunityId: string,
  input: {
    version: number;
    engagementCode: string;
    engagementName: string;
    projects: Array<{ code: string; name: string }>;
  },
) {
  return api<{ id: string; projects: Array<{ id: string; code: string; name: string }> }>(
    `/opportunities/${opportunityId}/convert`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function listProjects(search = '', status = '') {
  const params = new URLSearchParams({ page: '1', pageSize: '100' });
  if (search) params.set('search', search);
  if (status) params.set('status', status);
  return api<{ items: OperationalProject[]; total: number }>(`/projects?${params.toString()}`);
}

export function listCalendarItems(start: string, end: string, projectId = '') {
  const params = new URLSearchParams({ start, end });
  if (projectId) params.set('projectId', projectId);
  return api<CalendarItem[]>(`/calendar/items?${params.toString()}`);
}

export function getPortfolioDashboard(from: string, to: string) {
  return api<PortfolioDashboard>(`/dashboard/portfolio?from=${from}&to=${to}`);
}

export function getMyWorkDashboard(from: string, to: string) {
  return api<MyWorkDashboard>(`/dashboard/my-work?from=${from}&to=${to}`);
}

export function listNotifications(unreadOnly = false) {
  return api<InternalNotification[]>(`/notifications?unreadOnly=${unreadOnly}`);
}

export function markNotificationRead(notificationId: string) {
  return api<InternalNotification>(`/notifications/${notificationId}/read`, { method: 'PATCH' });
}

export function listNotificationPreferences() {
  return api<NotificationPreference[]>('/notifications/preferences/current');
}

export function saveNotificationPreference(input: NotificationPreference) {
  return api<NotificationPreference>('/notifications/preferences/current', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function listCalendarParticipants() {
  return api<CalendarParticipant[]>('/calendar/participants');
}

export function createCalendarEvent(input: CalendarEventInput) {
  return api<CalendarItem>('/calendar/events', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateCalendarEvent(
  eventId: string,
  input: CalendarEventInput & { version: number },
) {
  return api<CalendarItem>(`/calendar/events/${eventId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteCalendarEvent(eventId: string, version: number) {
  return api<void>(`/calendar/events/${eventId}?version=${version}`, { method: 'DELETE' });
}

export function getCalendarFeedStatus() {
  return api<CalendarFeedStatus>('/calendar/feed-token');
}

export function rotateCalendarFeed() {
  return api<{ feedUrl: string; createdAt: string }>('/calendar/feed-token/rotate', {
    method: 'POST',
  });
}

export function revokeCalendarFeed() {
  return api<void>('/calendar/feed-token', { method: 'DELETE' });
}

export function getProject(projectId: string) {
  return api<OperationalProject>(`/projects/${projectId}`);
}

export function listProjectTemplates() {
  return api<ProjectTemplate[]>('/project-templates');
}

export function createProjectTemplate(input: {
  name: string;
  description?: string;
  configuration: ProjectTemplateConfiguration;
}) {
  return api<ProjectTemplate>('/project-templates', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateProjectTemplate(
  templateId: string,
  input: {
    name: string;
    description?: string;
    configuration: ProjectTemplateConfiguration;
    active: boolean;
    version: number;
  },
) {
  return api<ProjectTemplate>(`/project-templates/${templateId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function configureProject(projectId: string, templateId: string, version: number) {
  return api<OperationalProject>(`/projects/${projectId}/configure`, {
    method: 'POST',
    body: JSON.stringify({ templateId, version }),
  });
}

export function listTasks(
  projectId: string,
  filters: { search?: string; stateId?: string; assigneeUserId?: string; dueTo?: string } = {},
) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
  return api<ProjectTask[]>(`/projects/${projectId}/tasks?${params.toString()}`);
}

export function createTask(
  projectId: string,
  input: {
    title: string;
    description?: string;
    type?: TaskType;
    priority?: TaskPriority;
    dueDate?: string;
    assigneeUserId?: string;
    parentTaskId?: string;
  },
) {
  return api<ProjectTask>(`/projects/${projectId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function transitionTask(
  projectId: string,
  taskId: string,
  input: { stateId: string; progressPercentage: number; version: number; reason?: string },
) {
  return api<ProjectTask>(`/projects/${projectId}/tasks/${taskId}/transition`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateProjectProgress(
  projectId: string,
  input: {
    completionPercentage: number;
    resultPercentage: number;
    trafficLight: ProjectTrafficLight;
    status: ProjectStatus;
    reason?: string;
    version: number;
  },
) {
  return api<OperationalProject>(`/projects/${projectId}/progress`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function createJournalEntry(
  projectId: string,
  input: { entryDate: string; content: string; taskId?: string },
) {
  return api(`/projects/${projectId}/journal`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function listPublications(projectId: string) {
  return api<PortalPublication[]>(`/projects/${projectId}/publications`);
}

export function listPublicationRecipients(projectId: string) {
  return api<Array<{ id: string; firstName: string; lastName: string; email: string }>>(
    `/projects/${projectId}/publications/recipients`,
  );
}

export function createPublicationDraft(
  projectId: string,
  input: {
    contentType: PublishableContentType;
    sourceEntityId: string;
    title: string;
    recipientUserIds: string[];
  },
) {
  return api<PortalPublication>(`/projects/${projectId}/publications`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function publishPublication(projectId: string, publicationId: string, version: number) {
  return api<PortalPublication>(`/projects/${projectId}/publications/${publicationId}/publish`, {
    method: 'POST',
    body: JSON.stringify({ version }),
  });
}

export function revokePublication(projectId: string, publicationId: string, version: number) {
  return api<PortalPublication>(`/projects/${projectId}/publications/${publicationId}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ version }),
  });
}

export function listInternalDocuments(projectId: string) {
  return api<InternalDocument[]>(`/documents?projectId=${encodeURIComponent(projectId)}`);
}

export function listProjectReports(projectId: string) {
  return api<ProjectReport[]>(`/projects/${projectId}/reports`);
}

export function createProjectReport(
  projectId: string,
  input: { title: string; sections: ReportSection[] },
) {
  return api<ProjectReport>(`/projects/${projectId}/reports`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function approveProjectReport(projectId: string, reportId: string, version: number) {
  return api<ProjectReport>(`/projects/${projectId}/reports/${reportId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ version }),
  });
}

export function reviseProjectReport(projectId: string, reportId: string) {
  return api<ProjectReport>(`/projects/${projectId}/reports/${reportId}/revisions`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function getProjectReportSchedule(projectId: string) {
  return api<ProjectReportSchedule>(`/projects/${projectId}/reports/schedule`);
}

export function saveProjectReportSchedule(
  projectId: string,
  input: {
    frequency: ReportScheduleFrequency;
    customIntervalDays?: number;
    nextRunAt?: string;
    titleTemplate: string;
    sections: ReportSection[];
    version?: number;
  },
) {
  return api<ProjectReportSchedule>(`/projects/${projectId}/reports/schedule`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export function listClientEmails(projectId: string) {
  return api<ClientEmailMessage[]>(`/projects/${projectId}/emails`);
}

export function createClientEmail(
  projectId: string,
  input: { recipientUserId: string; sourceReportId?: string; subject: string; body: string },
) {
  return api<ClientEmailMessage>(`/projects/${projectId}/emails`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function sendClientEmail(projectId: string, messageId: string, version: number) {
  return api<ClientEmailMessage>(`/projects/${projectId}/emails/${messageId}/send`, {
    method: 'POST',
    body: JSON.stringify({ version }),
  });
}

export function getAiStatus(projectId: string) {
  return api<AiStatus>(`/projects/${projectId}/ai/status`);
}

export function listAiDrafts(projectId: string) {
  return api<AiDraft[]>(`/projects/${projectId}/ai/drafts`);
}

export function createAiPreview(
  projectId: string,
  input: {
    periodStart: string;
    periodEnd: string;
    sourceKinds: AiSourceKind[];
    instructions: string;
  },
) {
  return api<AiGenerationPreview>(`/projects/${projectId}/ai/previews`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function confirmAiGeneration(projectId: string, generationId: string, version: number) {
  return api<AiDraft>(`/projects/${projectId}/ai/generations/${generationId}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ version }),
  });
}

export function reviewAiDraft(
  projectId: string,
  draftId: string,
  input: { version: number; content: AiDraftContent; reviewNote?: string },
) {
  return api<AiDraft>(`/projects/${projectId}/ai/drafts/${draftId}/review`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function listClientPortalProjects() {
  return api<ClientPortalProject[]>('/portal/projects');
}

export function getClientPortalProject(projectId: string) {
  return api<ClientPortalProjectDetail>(`/portal/projects/${projectId}`);
}

export function listClientProjectAccess(projectId: string) {
  return api<ClientProjectAccessCandidate[]>(`/projects/${projectId}/client-access`);
}

export function grantClientProjectAccess(projectId: string, userId: string) {
  return api(`/projects/${projectId}/client-access/${userId}`, { method: 'POST' });
}

export function revokeClientProjectAccess(projectId: string, userId: string) {
  return api<void>(`/projects/${projectId}/client-access/${userId}`, { method: 'DELETE' });
}

export function listAdminUsers() {
  return api<AdminUser[]>('/admin/users');
}

export function listAdminInvitations() {
  return api<AdminInvitation[]>('/admin/invitations');
}

export function createAdminInvitation(input: {
  email: string;
  role: AuthUser['role'];
  collaboratorVisibility?: CollaboratorVisibility;
}) {
  return api<{ id: string; expiresAt: string; acceptUrl: string }>('/admin/invitations', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateAdminUser(
  userId: string,
  input: {
    role: AuthUser['role'];
    collaboratorVisibility?: CollaboratorVisibility;
    version: number;
  },
) {
  return api<Pick<AdminUser, 'id' | 'role' | 'collaboratorVisibility' | 'status' | 'version'>>(
    `/admin/users/${userId}`,
    { method: 'PATCH', body: JSON.stringify({ ...input, confirmation: 'CONFERMO' }) },
  );
}

export function adminUserAction(
  userId: string,
  action: 'suspend' | 'activate' | 'sessions/revoke',
) {
  return api<void>(`/admin/users/${userId}/${action}`, {
    method: 'POST',
    body: JSON.stringify({ confirmation: 'CONFERMO' }),
  });
}

export function revokeAdminInvitation(invitationId: string) {
  return api<void>(`/admin/invitations/${invitationId}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ confirmation: 'CONFERMO' }),
  });
}

export function listCustomFieldDefinitions(entityType: CustomEntityType) {
  return api<CustomFieldDefinition[]>(
    `/project-settings/custom-fields/definitions?entityType=${entityType}`,
  );
}

export function createCustomFieldDefinition(input: {
  entityType: CustomEntityType;
  key: string;
  label: string;
  fieldType: CustomFieldType;
  options?: string[];
  required: boolean;
}) {
  return api<CustomFieldDefinition>('/project-settings/custom-fields/definitions', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function getAdminConfiguration() {
  return api<OperationalSettings>('/admin/configuration');
}

export function listOpenAiModels(refresh = false) {
  return api<OpenAiModelCatalog>(`/admin/openai/models${refresh ? '?refresh=true' : ''}`);
}

export function saveAdminConfiguration(input: OperationalSettings) {
  return api<OperationalSettings>('/admin/configuration', {
    method: 'PUT',
    body: JSON.stringify({ ...input, providerStatus: undefined, confirmation: 'CONFERMO' }),
  });
}

export function listProjectPermissions(projectId: string) {
  return api<ProjectPermissionCandidate[]>(`/admin/projects/${projectId}/permissions`);
}

export function saveProjectPermission(
  projectId: string,
  userId: string,
  input: ProjectPermissionCandidate['permission'],
) {
  return api<ProjectPermissionCandidate['permission']>(
    `/admin/projects/${projectId}/permissions/${userId}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        canManage: input.canManage,
        canPublish: input.canPublish,
        canApprove: input.canApprove,
        canGenerateAi: input.canGenerateAi,
        version: input.version,
        confirmation: 'CONFERMO',
      }),
    },
  );
}

export function listAuditEvents(filters: {
  action?: string;
  entityType?: string;
  actorUserId?: string;
  from?: string;
  to?: string;
}) {
  const params = new URLSearchParams({ page: '1', pageSize: '50' });
  for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
  return api<{ items: AuditEvent[]; total: number; page: number; pageSize: number }>(
    `/admin/audit?${params.toString()}`,
  );
}

export function listEconomicUsers() {
  return api<EconomicUser[]>('/economics/rate-users');
}

export function listHourlyRates() {
  return api<InternalHourlyRate[]>('/economics/hourly-rates');
}

export function createHourlyRate(input: {
  userId: string;
  rateCentsPerHour: number;
  validFrom: string;
  validTo?: string;
}) {
  return api<InternalHourlyRate>('/economics/hourly-rates', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function getEconomicsSummary(projectId: string) {
  return api<EconomicsSummary>(`/projects/${projectId}/economics/summary`);
}

export function saveProjectBudget(
  projectId: string,
  input: {
    revenueBudgetCents: number;
    hoursBudgetMinutes: number;
    laborCostBudgetCents: number;
    expenseBudgetCents: number;
    externalCostBudgetCents: number;
    timeTrackingRequired: boolean;
    version?: number;
  },
) {
  return api<ProjectBudget>(`/projects/${projectId}/economics/budget`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function listTimeEntries(projectId: string) {
  return api<TimeEntry[]>(`/projects/${projectId}/economics/time-entries`);
}

export function createTimeEntry(
  projectId: string,
  input: { userId?: string; taskId?: string; workDate: string; minutes: number; note?: string },
) {
  return api<TimeEntry>(`/projects/${projectId}/economics/time-entries`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateTimeEntry(
  projectId: string,
  entryId: string,
  input: {
    userId?: string;
    taskId?: string;
    workDate: string;
    minutes: number;
    note?: string;
    version: number;
  },
) {
  return api<TimeEntry>(`/projects/${projectId}/economics/time-entries/${entryId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function createProjectExpense(
  projectId: string,
  input: { description: string; competenceDate: string; amountCents: number; documentId?: string },
) {
  return api<ProjectExpense>(`/projects/${projectId}/economics/expenses`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function decideProjectExpense(
  projectId: string,
  expenseId: string,
  status: Exclude<EconomicRecordStatus, 'PENDING'>,
  version: number,
  decisionNote?: string,
) {
  return api<ProjectExpense>(`/projects/${projectId}/economics/expenses/${expenseId}/decision`, {
    method: 'POST',
    body: JSON.stringify({ status, version, ...(decisionNote ? { decisionNote } : {}) }),
  });
}

export function createExternalCost(
  projectId: string,
  input: {
    description: string;
    supplierName?: string;
    competenceDate: string;
    amountCents: number;
    documentId?: string;
  },
) {
  return api<ExternalCost>(`/projects/${projectId}/economics/external-costs`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function decideExternalCost(
  projectId: string,
  costId: string,
  status: Exclude<EconomicRecordStatus, 'PENDING'>,
  version: number,
  decisionNote?: string,
) {
  return api<ExternalCost>(`/projects/${projectId}/economics/external-costs/${costId}/decision`, {
    method: 'POST',
    body: JSON.stringify({ status, version, ...(decisionNote ? { decisionNote } : {}) }),
  });
}

export function createProjectRevenue(
  projectId: string,
  input: { description: string; competenceDate: string; amountCents: number },
) {
  return api<ProjectRevenue>(`/projects/${projectId}/economics/revenues`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
